import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, ArrowLeft, Send, Mail, User, Building2, MessageSquare, Loader2, CheckCircle, Package, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../lib/apiUrl';

const INQUIRY_TYPES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'sales', label: 'Sales / Pricing' },
  { value: 'support', label: 'Technical Support' },
  { value: 'partnership', label: 'Partnership Opportunity' },
  { value: 'demo', label: 'Request a Demo' },
  { value: 'feedback', label: 'Feedback / Suggestion' },
];

const PLAN_OPTIONS = [
  { 
    value: 'none', 
    label: 'Not sure yet',
    description: 'I need more information'
  },
  { 
    value: 'standard', 
    label: 'Standard Pack - $49 USD',
    description: '1 User, 3 Assessments, One-time payment'
  },
  { 
    value: 'pro', 
    label: 'Pro Pack - $99 USD',
    description: '3 Users, 5 Assessments, One-time payment (93 Controls)'
  },
];

const ContactUs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlan = searchParams.get('plan') || 'none';
  const { user, isAuthenticated } = useAuth();
  const userPlan = user?.subscription?.plan || 'none';
  
  // Determine navigation destinations based on auth status
  const logoDestination = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? '/dashboard' : '/';
  const backButtonDestination = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? '/dashboard' : '/';
  const backButtonText = isAuthenticated && (userPlan === 'standard' || userPlan === 'pro') ? 'Back to Dashboard' : 'Back to Home';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: initialPlan !== 'none' ? 'sales' : 'general',
    plan_interest: initialPlan,
    team_size: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-md w-full text-center"
        >
          <div className="inline-flex items-center justify-center p-4 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-slate-900 mb-3">
            Message Sent!
          </h2>
          <p className="text-slate-600 mb-6">
            Thank you for reaching out. Our team will review your message and get back to you within 24-48 hours.
          </p>
          {formData.plan_interest !== 'none' && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>Plan Interest:</strong> {PLAN_OPTIONS.find(p => p.value === formData.plan_interest)?.label}
              </p>
            </div>
          )}
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-slate-900 hover:bg-slate-800"
            >
              Back to Home
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  company: '',
                  inquiry_type: 'general',
                  plan_interest: 'none',
                  team_size: '',
                  message: '',
                });
              }}
              className="w-full"
            >
              Send Another Message
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to={logoDestination} className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-slate-800" />
              <span className="font-heading font-bold text-xl text-slate-900">AIVORA Comply360</span>
            </Link>
            <Button variant="outline" asChild>
              <Link to={backButtonDestination}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {backButtonText}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading font-bold text-4xl text-slate-900 mb-4">
              Get in Touch
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              Have questions about AIVORA Comply360? Want to learn how we can help with your ISO 27001 compliance journey? We would love to hear from you.
            </p>

            {/* Plan Comparison Mini */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                Our Plans
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium text-slate-900">Standard Pack - $49</p>
                    <p className="text-sm text-slate-500">1 User, 3 Assessments</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium text-slate-900">Pro Pack - $99 <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full ml-1">BEST VALUE</span></p>
                    <p className="text-sm text-slate-500">3 Users, 5 Assessments (93 Controls)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Email Us</h3>
                  <a href="mailto:support@aivora-techlabs.com" className="text-blue-600 hover:underline">
                    support@aivora-techlabs.com
                  </a>
                  <p className="text-slate-500 text-sm mt-1">We would love to hear from you</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <MessageSquare className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Sales Inquiries</h3>
                  <a href="mailto:contact@aivora-techlabs.com" className="text-blue-600 hover:underline">
                    contact@aivora-techlabs.com
                  </a>
                  <p className="text-slate-500 text-sm mt-1">Feel free to reach us for any sales enquiries</p>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-10 pt-8 border-t border-slate-200">
              <p className="text-slate-500 text-sm mb-4">Trusted by organizations worldwide</p>
              <div className="flex items-center gap-6 text-slate-400">
                <div className="text-center">
                  <div className="font-heading font-bold text-xl text-slate-700">ISO 27001</div>
                  <div className="text-xs">Compliant</div>
                </div>
                <div className="w-px h-8 bg-slate-200"></div>
                <div className="text-center">
                  <div className="font-heading font-bold text-xl text-slate-700">256-bit</div>
                  <div className="text-xs">Encryption</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <h2 className="font-heading font-bold text-xl text-slate-900 mb-6" data-testid="contact-form-title">
                Send us a Message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                      data-testid="contact-name-input"
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-xs mt-1" data-testid="contact-name-error">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="contact-email-input"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="contact-email-error">{errors.email}</p>}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      name="company"
                      type="text"
                      placeholder="Acme Corporation"
                      value={formData.company}
                      onChange={handleInputChange}
                      className={`pl-10 ${errors.company ? 'border-red-500' : ''}`}
                      data-testid="contact-company-input"
                    />
                  </div>
                  {errors.company && <p className="text-red-500 text-xs mt-1" data-testid="contact-company-error">{errors.company}</p>}
                </div>

                {/* Team Size */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Team Size <span className="text-slate-400 text-xs">(optional)</span>
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <select
                      name="team_size"
                      value={formData.team_size}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="contact-team-size-select"
                    >
                      <option value="">Select team size</option>
                      <option value="1">Just me</option>
                      <option value="2-5">2-5 people</option>
                      <option value="6-10">6-10 people</option>
                      <option value="11-25">11-25 people</option>
                      <option value="26-50">26-50 people</option>
                      <option value="51+">51+ people</option>
                    </select>
                  </div>
                </div>

                {/* Plan Interest */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Plan Interest <span className="text-slate-400 text-xs">(helps us serve you better)</span>
                  </label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <select
                      name="plan_interest"
                      value={formData.plan_interest}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="contact-plan-interest-select"
                    >
                      {PLAN_OPTIONS.map(plan => (
                        <option key={plan.value} value={plan.value}>
                          {plan.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formData.plan_interest !== 'none' && (
                    <p className="text-xs text-slate-500 mt-1" data-testid="contact-plan-description">
                      {PLAN_OPTIONS.find(p => p.value === formData.plan_interest)?.description}
                    </p>
                  )}
                </div>

                {/* Inquiry Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Inquiry Type
                  </label>
                  <select
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="contact-inquiry-type-select"
                  >
                    {INQUIRY_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    placeholder="Tell us how we can help you..."
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                      errors.message ? 'border-red-500' : 'border-slate-300'
                    }`}
                    data-testid="contact-message-textarea"
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1" data-testid="contact-message-error">{errors.message}</p>}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 text-lg font-semibold"
                  disabled={loading}
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
                <p className="text-center text-xs leading-5 text-slate-500" data-testid="contact-privacy-notice">
                  We use your details to respond to this inquiry. See our <a href="/privacy" className="text-blue-600 hover:underline" data-testid="contact-privacy-link">Privacy Policy</a>.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default ContactUs;
